import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Controller, useWatch } from 'react-hook-form-v7';
import type { UseFormReturn } from 'react-hook-form-v7';
import { useTranslation } from 'react-i18next';
import { CircleHelp } from '@parrot-software/design-system/icons';

import { useEmployeeFormContext } from '@personnel/presentation/components/form/EmployeeForm/hooks';
import {
  styles,
  EMPLOYEE_FORM_IDS,
  FormFieldHoverHelp,
} from '@personnel/presentation/components/form/EmployeeForm/shared';
import {
  hasRoleRequiredPermissions,
  PERMISSIONS_THAT_REQUIRE_CREDENTIALS,
  PERMISSIONS_THAT_REQUIRE_PIN,
} from '@personnel/presentation/components/form/EmployeeForm/utils';
import { FormSelectField, FormTextField } from '@personnel/presentation/components/shared';
import { usePersonnelProfiles, usePersonnelStores, useRoleList } from '@personnel/presentation/hooks';
import type { EmployeeFormData } from '@personnel/presentation/types';

// No code or slug on the roles endpoint: admin.store marks the super admin, its name is the fallback.
const SUPER_ADMIN_ROLE_NAME = 'Super Admin';
const SUPER_ADMIN_PERMISSIONS = ['admin.store'] as const;

interface UserDataSectionProps {
  form: UseFormReturn<EmployeeFormData>;
}

/**
 * Who the user is: their role, the position they hold and their name.
 *
 * Choosing a role is what decides which of the cards below are open, so this section keeps the two
 * internal flags in step with the role. They are set without dirtying the form: the administrator
 * did not change them, the role did.
 *
 * @param {UserDataSectionProps} props - Component properties
 */
export const UserDataSection = ({ form }: UserDataSectionProps): React.ReactElement => {
  const { t } = useTranslation('personnel-employees');
  const { control, setValue } = useEmployeeFormContext(form);
  const { roles, isLoading: rolesLoading } = useRoleList();
  const { data: profiles = [], isLoading: profilesLoading } = usePersonnelProfiles();
  const { data: stores = [] } = usePersonnelStores();

  const memoizedProfiles = useMemo(() => profiles, [profiles]);

  const roleUuid = useWatch({ control, name: 'roleUuid' });
  const lastProcessedRoleUuidRef = useRef<string | null>(null);

  const applyRolePermissions = useCallback(
    (nextRoleUuid: string) => {
      const selectedRole = roles.find((role) => role.uuid === nextRoleUuid);

      if (selectedRole && form.getValues('roleName') !== selectedRole.name) {
        setValue('roleName', selectedRole.name, { shouldDirty: false });
      }

      setValue('_credentialsEnabled', hasRoleRequiredPermissions(selectedRole, PERMISSIONS_THAT_REQUIRE_CREDENTIALS), {
        shouldValidate: true,
        shouldDirty: false,
      });

      setValue('_pinEnabled', hasRoleRequiredPermissions(selectedRole, PERMISSIONS_THAT_REQUIRE_PIN), {
        shouldValidate: true,
        shouldDirty: false,
      });

      return selectedRole;
    },
    [form, roles, setValue],
  );

  // A super admin operates on the whole account, so that role turns on the access to every store.
  const assignEveryStore = useCallback(() => {
    if (stores.length === 0) return;

    setValue('allStoresAssigned', true, { shouldDirty: true, shouldValidate: true });
    setValue(
      'assignedStores',
      stores.map(({ name, uuid }) => ({ name, uuid })),
      { shouldDirty: true, shouldValidate: true },
    );
  }, [setValue, stores]);

  const handleRoleChange = useCallback(
    (nextRoleUuid: string) => {
      setValue('roleUuid', nextRoleUuid, { shouldDirty: true });

      const selectedRole = applyRolePermissions(nextRoleUuid);

      if (
        selectedRole?.name === SUPER_ADMIN_ROLE_NAME ||
        hasRoleRequiredPermissions(selectedRole, SUPER_ADMIN_PERMISSIONS)
      ) {
        assignEveryStore();
      }
    },
    [applyRolePermissions, assignEveryStore, setValue],
  );

  useEffect(() => {
    if (!roleUuid || roles.length === 0) return;
    if (lastProcessedRoleUuidRef.current === roleUuid) return;

    lastProcessedRoleUuidRef.current = roleUuid;
    applyRolePermissions(roleUuid);
  }, [applyRolePermissions, roleUuid, roles]);

  return (
    <div className={styles.container}>
      <div>
        <h2 className={styles.header.cardTitle}>{t('form.userDataSection.title')}</h2>
        <p className={styles.header.description}>{t('form.userDataSection.description')}</p>
      </div>

      <div className={styles.gridGroup}>
        <Controller
          control={control}
          name="roleUuid"
          render={({ field, fieldState }) => (
            <FormSelectField
              disabled={rolesLoading}
              error={fieldState.error?.message}
              field={field}
              getOptionLabel={(role) => role.name}
              getOptionValue={(role) => role.uuid}
              id={EMPLOYEE_FORM_IDS.ROLE}
              label={t('form.roleLabel')}
              labelClassName="flex gap-1"
              labelIcon={
                <FormFieldHoverHelp
                  helpText={t('form.roleHelpText')}
                  icon={<CircleHelp className="size-4 text-blue-700" />}
                />
              }
              onValueChange={handleRoleChange}
              options={roles}
              placeholder={t('form.rolePlaceholder')}
              required
            />
          )}
        />
      </div>

      <div className={styles.gridGroup}>
        <Controller
          control={control}
          name="profileId"
          render={({ field, fieldState }) => (
            <FormSelectField
              disabled={profilesLoading}
              error={fieldState.error?.message}
              field={field}
              getOptionKey={(profile) => String(profile.id)}
              getOptionLabel={(profile) => profile.name}
              getOptionValue={(profile) => String(profile.id)}
              id={EMPLOYEE_FORM_IDS.PROFILE}
              label={t('form.profileLabel')}
              labelClassName="flex gap-1"
              labelIcon={
                <FormFieldHoverHelp
                  helpText={t('form.profileHelpText')}
                  icon={<CircleHelp className="size-4 text-blue-700" />}
                />
              }
              options={memoizedProfiles}
              placeholder={t('form.profilePlaceholder')}
              required
              triggerClassName="h-9 py-0 text-sm"
            />
          )}
        />
        <Controller
          control={control}
          name="firstName"
          render={({ field, fieldState }) => (
            <FormTextField
              error={fieldState.error?.message}
              field={field}
              id={EMPLOYEE_FORM_IDS.FIRST_NAME}
              label={t('form.nameLabel')}
              maxLength={255}
              placeholder={t('form.namePlaceholder')}
              required
            />
          )}
        />
        <Controller
          control={control}
          name="lastName"
          render={({ field, fieldState }) => (
            <FormTextField
              error={fieldState.error?.message}
              field={field}
              id={EMPLOYEE_FORM_IDS.LAST_NAME}
              label={t('form.lastnameLabel')}
              maxLength={255}
              placeholder={t('form.lastnamePlaceholder')}
              required
            />
          )}
        />
      </div>
    </div>
  );
};
